import { useState, useEffect, useRef, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { has, isArray } from "lodash";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { socketConnection } from "../../services/socket";
import moment from "moment";

const SESSION_DURATION_MS = 24 * 60 * 60 * 1000; // 24 horas em ms

const useAuth = () => {
  const history = useHistory();
  const [isAuth, setIsAuth] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState({});
  const [socket, setSocket] = useState({});

  // Trava global: impede múltiplos disparos de logout
  const isHandlingLogout = useRef(false);

  // Função interna de logout — protegida pela trava
  const performLogout = useCallback((message) => {
    if (isHandlingLogout.current) return;
    isHandlingLogout.current = true;

    localStorage.removeItem("token");
    localStorage.removeItem("cshow");
    localStorage.removeItem("login_time");
    api.defaults.headers.Authorization = undefined;
    setIsAuth(false);
    setUser({});

    if (message) {
      // toastId garante apenas 1 notificação mesmo com múltiplas chamadas
      toast.warn(message, { toastId: "session-expired", autoClose: 4000 });
    }

    history.push("/login");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Interceptors registrados UMA ÚNICA VEZ via useEffect
  useEffect(() => {
    const reqInterceptor = api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem("token");
        if (token) {
          config.headers["Authorization"] = `Bearer ${JSON.parse(token)}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    const resInterceptor = api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        const isRefreshRequest = originalRequest?.url?.includes('/auth/refresh_token');

        if ((error?.response?.status === 401 || error?.response?.status === 403) && !originalRequest._retry && !isRefreshRequest) {
          originalRequest._retry = true;
          try {
            const { data } = await api.post("/auth/refresh_token");
            if (data) {
              localStorage.setItem("token", JSON.stringify(data.token));
              api.defaults.headers.Authorization = `Bearer ${data.token}`;
            }
            return api(originalRequest);
          } catch (_) {
            performLogout("Sua sessão expirou. Faça login novamente.");
            return Promise.reject(error);
          }
        }

        return Promise.reject(error);
      }
    );

    // Limpar interceptors ao desmontar (evita acúmulo)
    return () => {
      api.interceptors.request.eject(reqInterceptor);
      api.interceptors.response.eject(resInterceptor);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Timer de sessão: verifica a cada minuto
  useEffect(() => {
    if (!isAuth) return;

    const checkSession = () => {
      const loginTime = localStorage.getItem("login_time");
      if (!loginTime) return;
      const elapsed = Date.now() - parseInt(loginTime, 10);
      if (elapsed >= SESSION_DURATION_MS) {
        performLogout("Sua sessão expirou após 24 horas. Faça login novamente.");
      }
    };

    checkSession(); // verificar imediatamente ao montar

    const timer = setInterval(checkSession, 60 * 1000);
    return () => clearInterval(timer);
  }, [isAuth]); // eslint-disable-line react-hooks/exhaustive-deps

  // Restore session on page load
  useEffect(() => {
    // Handle demo login: landing page (api.chatprime.app.br) passes token via URL
    // because localStorage is isolated per domain and cannot be shared cross-subdomain
    const urlParams = new URLSearchParams(window.location.search);
    const demoToken = urlParams.get('demo_token');
    const demoTs = urlParams.get('demo_ts');
    if (demoToken && demoTs) {
      localStorage.setItem('token', JSON.stringify(demoToken));
      localStorage.setItem('login_time', demoTs);
      localStorage.setItem('isDemo', 'true');
      localStorage.setItem('demoStartTime', demoTs);
      window.history.replaceState({}, document.title, '/');
      // Notifica DemoBanner (que já montou antes deste effect rodar)
      window.dispatchEvent(new Event('chatprime-demo-ready'));
    }

    const token = localStorage.getItem("token");
    (async () => {
      if (token) {
        const loginTime = localStorage.getItem("login_time");
        if (loginTime) {
          const elapsed = Date.now() - parseInt(loginTime, 10);
          if (elapsed >= SESSION_DURATION_MS) {
            performLogout("Sua sessão expirou após 24 horas. Faça login novamente.");
            setLoading(false);
            return;
          }
        }

        try {
          const currentToken = JSON.parse(token);
          api.defaults.headers.Authorization = `Bearer ${currentToken}`;
          const { data } = await api.post("/auth/refresh_token");
          api.defaults.headers.Authorization = `Bearer ${data.token}`;
          setUser(data.user);
          setIsAuth(true);

          // Bloquear empresa suspensa ou vencida — redirecionar para planos
          const cId = data.user?.companyId;
          if (cId && cId !== 1) {
            const companyStatus = data.user?.company?.status;
            const dueDate = data.user?.company?.dueDate;
            const isBlocked =
              companyStatus === false ||
              (dueDate && new Date(dueDate) < new Date());
            if (isBlocked) {
              history.push("/planos");
            }
          }
        } catch (err) {
          localStorage.removeItem('token');
          localStorage.removeItem('login_time');
          api.defaults.headers.Authorization = undefined;
        }
      }
      setLoading(false);
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Socket: atualizar dados do usuário em tempo real
  useEffect(() => {
    if (Object.keys(user).length && user.id > 0) {
      let io;
      if (!Object.keys(socket).length) {
        io = socketConnection({ user });
        setSocket(io);
      } else {
        io = socket;
      }

      io.on(`company-${user.companyId}-user`, (data) => {
        if (data.action === "update" && data.user.id === user.id) {
          setUser(data.user);
        }
      });

      io.on(`company-${user.companyId}-payment`, (paymentData) => {
        if (paymentData.action === "blocked") {
          toast.warn("Acesso suspenso. Realize o pagamento para reativar.", { autoClose: false });
          history.push("/planos");
        }
        if (paymentData.action === "approved" || paymentData.action === "CONCLUIDA") {
          toast.success("Pagamento confirmado! Acesso reativado.", { autoClose: 4000 });
          // Recarrega a sessão para refletir o novo status/dueDate
          setTimeout(() => { window.location.href = "/tickets"; }, 2000);
        }
      });

      return () => {
        io.off(`company-${user.companyId}-user`);
        io.off(`company-${user.companyId}-payment`);
      };
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Login
  const handleLogin = async (userData) => {
    setLoading(true);
    isHandlingLogout.current = false; // Resetar trava ao fazer novo login

    try {
      const { data } = await api.post("/auth/login", userData);
      const { user: { company } } = data;

      if (has(company, "companieSettings") && isArray(company.companieSettings[0])) {
        const setting = company.companieSettings[0].find(
          (s) => s.key === "campaignsEnabled"
        );
        if (setting && setting.value === "true") {
          localStorage.setItem("cshow", null);
        }
      }

      if (has(company, "companieSettings") && isArray(company.companieSettings[0])) {
        const setting = company.companieSettings[0].find(
          (s) => s.key === "sendSignMessage"
        );
        if (setting) {
          const signEnable = setting.value === "enable";
          if (setting.value === "enabled") {
            localStorage.setItem("sendSignMessage", signEnable);
          }
        }
      }

      localStorage.setItem("profileImage", data.user.profileImage);

      moment.locale("pt-br");
      let dueDate;
      if (data.user.company.id === 1) {
        dueDate = "2999-12-31T00:00:00.000Z";
      } else {
        dueDate = data.user.company.dueDate;
      }

      const vencimento = moment(dueDate).format("DD/MM/yyyy");
      const diff = moment(dueDate).diff(moment(moment()).format());
      const before = moment(moment().format()).isBefore(dueDate);
      const dias = moment.duration(diff).asDays();

      if (before === true) {
        localStorage.setItem("login_time", Date.now().toString());
        localStorage.setItem("token", JSON.stringify(data.token));
        localStorage.setItem("companyDueDate", vencimento);
        api.defaults.headers.Authorization = `Bearer ${data.token}`;
        setUser(data.user);
        setIsAuth(true);
        toast.success(i18n.t("auth.toasts.success"));

        if (Math.round(dias) < 5) {
          toast.warn(
            `Sua assinatura vence em ${Math.round(dias)} ${
              Math.round(dias) === 1 ? "dia" : "dias"
            } `
          );
        }

        history.push("/tickets");
        setLoading(false);
      } else {
        api.defaults.headers.Authorization = `Bearer ${data.token}`;
        setIsAuth(true);
        toast.warn(
          "Seu período de teste gratuito encerrou. Escolha um plano para continuar.",
          { autoClose: 6000 }
        );
        history.push("/planos");
        setLoading(false);
      }
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  // Logout manual
  const handleLogout = async () => {
    setLoading(true);

    try {
      await api.delete("/auth/logout");
    } catch (err) {
      // ignorar erro no endpoint, prosseguir com logout local
    }

    performLogout(null);
    setLoading(false);
  };

  const getCurrentUserInfo = async () => {
    try {
      const { data } = await api.get("/auth/me");
      return data;
    } catch (_) {
      return null;
    }
  };

  return {
    isAuth,
    user,
    loading,
    handleLogin,
    handleLogout,
    getCurrentUserInfo,
    socket,
  };
};

export default useAuth;